import { iDataCenterDoc } from "@/app/models/interfaceProject";
import { useEffect, useState } from "react";
import Modal from "../Elements/Modal";
import { DataCenterDoc, OriginFile, TypeFile } from "@/app/constants/enums";
import { Copy, Check, Search, Loader2, FileText, FileSpreadsheet, File } from "lucide-react";
import { Constantes } from "@/app/constants/constantsGlobal";
import { getDriveMetadata } from "@/app/services/driveService";
import { iDriveMetadata } from "@/app/models/drive";
import { DriveExcelIcon } from "../Elements/Icon/DriveExcelIcon";
import { DrivePdfIcon } from "../Elements/Icon/DrivePdfIcon";
import { useUser } from "@/app/context/UserContext";

interface DatacenterServiceAccountProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (data: iDataCenterDoc) => void;
}

// ── MIME Types ────────────────────────────────────────────────────────────────
// Google Workspace
const MIME_G_SPREADSHEET = "application/vnd.google-apps.spreadsheet";
const MIME_G_DOC         = "application/vnd.google-apps.document";
// Microsoft Office (escritorio)
const MIME_XLSX          = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const MIME_XLS           = "application/vnd.ms-excel";
const MIME_DOCX          = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const MIME_DOC_OLD       = "application/msword";
// PDF
const MIME_PDF           = "application/pdf";

const SPREADSHEET_MIMES  = [MIME_G_SPREADSHEET, MIME_XLSX, MIME_XLS];
const DOC_MIMES          = [MIME_G_DOC, MIME_DOCX, MIME_DOC_OLD, MIME_PDF];

function isSpreadsheetMime(mimeType: string) {
  return SPREADSHEET_MIMES.includes(mimeType);
}

function isDocMime(mimeType: string) {
  return DOC_MIMES.includes(mimeType);
}

function getFileIcon(mimeType: string) {
  if (isSpreadsheetMime(mimeType)) return <DriveExcelIcon size={24} />;
  if (mimeType === MIME_PDF)       return <DrivePdfIcon size={30}/>;
  if (isDocMime(mimeType))         return <FileText size={24} className="text-blue-500" />;
  return <File size={14} className="text-gray-400" />;
}

function getMimeLabel(mimeType: string) {
  const labels: Record<string, string> = {
    [MIME_G_SPREADSHEET]: "Google Sheets",
    [MIME_G_DOC]:         "Google Doc",
    [MIME_XLSX]:          "Excel (.xlsx)",
    [MIME_XLS]:           "Excel (.xls)",
    [MIME_DOCX]:          "Word (.docx)",
    [MIME_DOC_OLD]:       "Word (.doc)",
    [MIME_PDF]:           "PDF",
  };
  return labels[mimeType] ?? mimeType;
}


function getChatbotFileType(
  mimeType: string,
  isQA: boolean
): TypeFile {
  if (isQA) return TypeFile.Qa;
  if (mimeType === MIME_PDF) { return TypeFile.Pdf; }
  if (isSpreadsheetMime(mimeType)) { return TypeFile.Excel;}
  if (isDocMime(mimeType)) {return TypeFile.Docs;}
  return TypeFile.Docs;
}

export default function DatacenterUploadGoogleDrive({
  isOpen,
  onClose,
  onUpload,
}: DatacenterServiceAccountProps) {
  const {  user, setUser} = useUser();
  const [fileUrl, setFileUrl]               = useState("");
  const [errorUrl, setErrorUrl]             = useState("");
  const [copied, setCopied]                 = useState(false);

  const [metadata, setMetadata]             = useState<iDriveMetadata | null>(null);
  const [loadingMeta, setLoadingMeta]       = useState(false);
  const [errorMeta, setErrorMeta]           = useState("");

  const [isQA, setIsQA]                     = useState(false);
  const [selectedColumn, setSelectedColumn] = useState("");
  const [errorColumn, setErrorColumn]       = useState("");

  useEffect(() => {
    if (!isOpen) resetValue();
  }, [isOpen]);

  const resetValue = () => {
    setFileUrl("");
    setErrorUrl("");
    setMetadata(null);
    setErrorMeta("");
    setIsQA(false);
    setSelectedColumn("");
    setErrorColumn("");
  };

  // ── Consultar metadatos ───────────────────────────────────────────────────
  const handleFetchMetadata = async () => {
    if (!fileUrl.trim()) {
      setErrorUrl("La URL del archivo es obligatoria.");
      return;
    }
    try {
      new URL(fileUrl.trim());
    } catch {
      setErrorUrl("Ingresa una URL válida (ej: https://drive.google.com/...).");
      return;
    }

    setErrorUrl("");
    setMetadata(null);
    setSelectedColumn("");
    setErrorColumn("");
    setIsQA(false);
    setLoadingMeta(true);
    setErrorMeta("");

    try {
      const result = await getDriveMetadata(fileUrl.trim(), user,  setUser);
      if (result.success && result.code === 200) {
        setMetadata(result.data);
      }else{
        setErrorMeta(result.message ); 
      } 
    } catch {
      setErrorMeta("No se pudo obtener información del archivo. Verifica la URL y los permisos.");
    } finally {
      setLoadingMeta(false);
    }
  };

  // ── Validar y subir ───────────────────────────────────────────────────────
  const handleUpload = () => {
    let hasError = false;    
    // 1. URL obligatoria
    if (!fileUrl.trim()) {
      setErrorUrl("La URL del archivo es obligatoria.");
      hasError = true;
    }

    // 2. Debe haberse consultado el archivo
    if (!metadata) {
      setErrorMeta("Debes consultar el archivo antes de agregarlo. Haz clic en el ícono de búsqueda.");
      hasError = true;
    }

    // 3. Si es spreadsheet y NO es Q&A, columna principal obligatoria
    if (metadata && isSpreadsheet && !isQA && !selectedColumn) {
      setErrorColumn("Debes seleccionar una columna principal.");
      hasError = true;
    }

    if (hasError) return;

    const data: iDataCenterDoc = {
      file_id_local:      crypto.randomUUID(),
      chunk_overlap:      DataCenterDoc.chunk_overlap,
      chunk_size:         DataCenterDoc.chunk_size,
      deleted:            false,
      file_type:          metadata!.metadata.mimeType,
      file_size:          0,
      type:               getChatbotFileType(metadata!.metadata.mimeType, isQA),
      name:               metadata!.metadata.name,
      columns:            allColumns,
      embedded_column:    isQA ? "" : selectedColumn,
      file_origin:        OriginFile.Drive,

      drive_id:           metadata!.metadata.id,
      drive_mime_type:    metadata!.metadata.mimeType,
      drive_version:      metadata!.metadata.version,
      drive_modified_time: metadata!.metadata.modifiedTime,
      drive_url:          fileUrl.trim(),
      created:            true, 
    };
    onUpload(data);
    resetValue();
  };

  // ── Derivados ─────────────────────────────────────────────────────────────
  const isSpreadsheet = isSpreadsheetMime(metadata?.metadata.mimeType ?? "");
  const isPdfOrDoc    = isDocMime(metadata?.metadata.mimeType ?? "");

  const allColumns: string[] = isSpreadsheet && metadata?.sheets
    ? Object.values(metadata.sheets).flat().map((c) => c.trim()).filter(Boolean)
    : [];

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-5 w-[700px]">
        <h2 className="text-base font-semibold text-gray-800 mb-1">
          Subir Fuente desde Drive
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          El archivo debe compartirse en modo lectura con la siguiente cuenta de servicio.
        </p>

        {/* ── Cuenta de Servicios ── */}
        <div className="mb-4">
          <label className="block text-gray-600 text-xs font-medium mb-1 uppercase tracking-wide">
            Cuenta de Servicios
          </label>
          <div className="relative flex items-center">
            <input
              type="text"
              value={Constantes.SERVICE_ACCOUNT_DRIVE}
              readOnly
              disabled
              className="w-full border border-gray-200 bg-gray-50 text-gray-500 pl-3 pr-10 py-2 text-xs n-fmr-input focus:outline-none cursor-not-allowed truncate"
            />
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(Constantes.SERVICE_ACCOUNT_DRIVE);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
              title="Copiar cuenta de servicios"
              className="absolute right-2 transition-colors"
            >
              {copied
                ? <Check size={18} className="text-green-500" />
                : <Copy size={18} className="text-gray-400 hover:text-blue-600" />
              }
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Esta cuenta está configurada por el administrador y no puede modificarse.
          </p>
        </div>

        {/* ── URL del archivo ── */}
        <div className="mb-4">
          <label className="block text-gray-600 text-xs font-medium mb-1 uppercase tracking-wide">
            URL del archivo
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="https://drive.google.com/file/d/..."
              value={fileUrl}
              onChange={(e) => {
                setFileUrl(e.target.value);
                setErrorUrl("");
                setMetadata(null);
                setSelectedColumn("");
                setIsQA(false);
                setErrorMeta("");
                setErrorColumn("");
              }}
              className="w-full border border-gray-300 pl-3 pr-3 py-2 text-sm n-fmr-input focus:outline-none focus:ring-1 focus:ring-blue-600"
            />
            <button
              type="button"
              onClick={handleFetchMetadata}
              disabled={loadingMeta}
              title="Consultar archivo en Drive"
              className="flex items-center btn-inca-secondary justify-center px-2 py-2 w-10 h-10 border border-blue-300 rounded-full bg-gray-100 transition-colors border shadow-md  hover:bg-gray-200  "
            >
              {loadingMeta
                ? <Loader2 size={15} className="animate-spin" />
                : <Search size={15} />
              }
            </button>
          </div>
          {errorUrl && (
            <p className="text-red-500 text-xs mt-1">{errorUrl}</p>
          )}
          <p className="text-xs text-gray-400 mt-1">
            Puedes ingresar la URL de un archivo individual.
          </p>
        </div>

        {/* ── Error metadatos ── */}
        {errorMeta && (
          <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 text-red-600 text-xs">
            {errorMeta}
          </div>
        )}

        {/* ── Panel de metadatos ── */}
        {metadata && (
          <div className="mb-4 border border-gray-200 bg-gray-50 divide-y divide-gray-200">

            {/* Fila 1 — Info básica */}
            <div className="flex items-center gap-3 px-3 py-2.5">
              <div className="flex-shrink-0">
                {getFileIcon(metadata.metadata.mimeType)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">
                  {metadata.metadata.name}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {getMimeLabel(metadata.metadata.mimeType)}
                  <span className="mx-1.5">·</span>
                  Modificado: {new Date(metadata.metadata.modifiedTime).toLocaleDateString("es-PE", {
                    day: "2-digit", month: "short", year: "numeric",
                  })}
                  <span className="mx-1.5">·</span>
                  v{metadata.metadata.version}
                </p>
              </div>
              {isPdfOrDoc && (
                <span className="flex-shrink-0 text-xs bg-blue-100 text-blue-600 px-2 py-0.5">
                  Documento
                </span>
              )}
              {isSpreadsheet && (
                <span className="flex-shrink-0 text-xs bg-green-100 text-green-700 px-2 py-0.5">
                  Hoja de cálculo
                </span>
              )}
            </div>

            {/* Fila 2 — Solo para Spreadsheet */}
            {isSpreadsheet && (
              <div className="px-3 py-2.5 space-y-2.5">

                {/* Toggle Q&A */}
                <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
                  <div className="relative">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={isQA}
                      onChange={(e) => {
                        setIsQA(e.target.checked);
                        setSelectedColumn("");
                        setErrorColumn("");
                      }}
                    />
                    <div className={`w-8 h-4 rounded-full transition-colors duration-200 ${isQA ? "bg-green-500" : "bg-gray-300"}`} />
                    <div className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform duration-200 ${isQA ? "translate-x-4" : "translate-x-0"}`} />
                  </div>
                  <span className="text-xs text-gray-600 font-medium">
                    Archivo de Preguntas y Respuestas
                  </span>
                  {isQA && <Check size={13} className="text-green-500" />}
                </label>

                {/* Columnas */}
                {allColumns.length > 0 && (
                  <div>
                    {isQA ? (
                      /* Q&A: nombres de columnas como texto/badges informativos */
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs text-gray-400 font-medium mr-0.5">Columnas:</span>
                        {allColumns.map((col) => (
                          <span
                            key={col}
                            className="text-xs px-2 py-0.5 bg-green-50 text-green-700 border border-green-200"
                          >
                            {col}
                          </span>
                        ))}
                      </div>
                    ) : (
                      /* No Q&A: select para elegir columna principal */
                      <div>
                        <label className="block text-xs text-gray-500 font-medium mb-1">
                          Columna principal
                        </label>
                        <select
                          value={selectedColumn}
                          onChange={(e) => {
                            setSelectedColumn(e.target.value);
                            setErrorColumn("");
                          }}
                          className="w-full border border-gray-300 bg-white text-sm text-gray-700 py-2 px-3 n-fmr-input appearance-none focus:outline-none focus:ring-1 focus:ring-blue-600"
                        >
                          <option value="">-- Selecciona una columna --</option>
                          {allColumns.map((col) => (
                            <option key={col} value={col}>{col}</option>
                          ))}
                        </select>
                        {errorColumn && (
                          <p className="text-red-500 text-xs mt-1">{errorColumn}</p>
                        )}
                        {selectedColumn && (
                          <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                            <Check size={11} />
                            Columna principal: <span className="font-medium">{selectedColumn}</span>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Acciones ── */}
        <div className="flex items-center justify-end gap-2 mt-2">
          <button
            type="button"
            onClick={() => { resetValue(); onClose(); }}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 text-sm n-fmr-input focus:outline-none focus:ring-1 focus:ring-blue-600 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleUpload}
            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 text-sm n-fmr-input focus:outline-none focus:ring-1 focus:ring-blue-600 transition-colors"
          >
            Agregar
          </button>
        </div>
      </div>
    </Modal>
  );
}